import { DEFAULT_VACATION_TABLE } from '../lib/finiquitos/calc';

const FORMULA_PREFIX = /^\s*=/;

export const FINIQUITOS_VACATION_TABLE_MODES = {
  legal: 'legal',
  contractualManual: 'contractual_manual',
  manualOverride: 'manual_override'
};

export const FINIQUITOS_VACATION_TABLE_MODE_OPTIONS = [
  { value: FINIQUITOS_VACATION_TABLE_MODES.legal, label: 'Tabla legal por defecto' },
  { value: FINIQUITOS_VACATION_TABLE_MODES.contractualManual, label: 'Tabla contractual/manual' },
  { value: FINIQUITOS_VACATION_TABLE_MODES.manualOverride, label: 'Override manual excepcional' }
];

function createStableId(prefix) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const FINIQUITOS_REASON_OPTIONS = [
  { value: 'renuncia_voluntaria', label: 'Renuncia voluntaria' },
  { value: 'terminacion_no_voluntaria', label: 'Terminación no voluntaria' },
  { value: 'mutuo_acuerdo', label: 'Mutuo acuerdo' },
  { value: 'termino_contrato', label: 'Término de contrato' },
  { value: 'rescicion', label: 'Rescisión' },
  { value: 'fallecimiento', label: 'Fallecimiento' },
  { value: 'otro', label: 'Otro' }
];

export const FINIQUITOS_BENEFIT_TYPE_OPTIONS = [
  { value: 'prestacion_superior', label: 'Prestación superior' },
  { value: 'caso_excepcional', label: 'Caso excepcional' }
];

const FINIQUITOS_BENEFIT_TYPE_VALUES = new Set(FINIQUITOS_BENEFIT_TYPE_OPTIONS.map(option => option.value));

export const FINIQUITOS_CONCEPT_PRESETS = {
  perceptions: [
    { value: 'salarios_devengados_pendientes', label: 'Salarios devengados pendientes' },
    { value: 'comisiones_pendientes', label: 'Comisiones pendientes' },
    { value: 'bonos_incentivos_pendientes', label: 'Bonos o incentivos pendientes' },
    { value: 'horas_extra_pendientes', label: 'Horas extra pendientes' },
    { value: 'ptu_pendiente', label: 'PTU pendiente' },
    { value: 'fondo_ahorro_favor', label: 'Fondo o caja de ahorro a favor' },
    { value: 'ajuste_aguinaldo', label: 'Ajuste de aguinaldo' },
    { value: 'ajuste_vacaciones', label: 'Ajuste de vacaciones' },
    { value: 'ajuste_prima_vacacional', label: 'Ajuste de prima vacacional' },
    { value: 'prestacion_contractual_pendiente', label: 'Prestación contractual pendiente' }
  ],
  deductions: [
    { value: 'isr_manual', label: 'ISR manual' },
    { value: 'cuotas_imss', label: 'Cuotas IMSS' },
    { value: 'prestamo_personal', label: 'Préstamo personal' },
    { value: 'anticipo_sueldo', label: 'Anticipo de sueldo' },
    { value: 'credito_infonavit', label: 'Crédito Infonavit' },
    { value: 'credito_fonacot', label: 'Crédito Fonacot' },
    { value: 'pension_alimenticia', label: 'Pensión alimenticia' },
    { value: 'adeudo_autorizado', label: 'Adeudo autorizado por trabajador' },
    { value: 'otras_deducciones', label: 'Otras deducciones' }
  ],
  benefits: [
    {
      value: 'prima_antiguedad',
      label: 'Prima de antigüedad',
      type: FINIQUITOS_BENEFIT_TYPE_OPTIONS[1].value,
      note: 'Validar procedencia legal o convenio antes de aprobar.'
    },
    {
      value: 'indemnizacion_constitucional',
      label: 'Indemnización constitucional 3 meses',
      type: FINIQUITOS_BENEFIT_TYPE_OPTIONS[1].value,
      note: 'Aplicable solo en escenarios de liquidación o convenio.'
    },
    {
      value: 'veinte_dias_por_anio',
      label: '20 días por año',
      type: FINIQUITOS_BENEFIT_TYPE_OPTIONS[1].value,
      note: 'Capturar solo si el caso o convenio lo requiere.'
    },
    {
      value: 'gratificacion_convenio',
      label: 'Gratificación por convenio',
      type: FINIQUITOS_BENEFIT_TYPE_OPTIONS[0].value
    },
    {
      value: 'bono_separacion',
      label: 'Bono de separación',
      type: FINIQUITOS_BENEFIT_TYPE_OPTIONS[0].value
    },
    {
      value: 'prestacion_superior_lft',
      label: 'Prestación superior a LFT',
      type: FINIQUITOS_BENEFIT_TYPE_OPTIONS[0].value
    }
  ]
};

export const FINIQUITOS_DEFAULT_EMPLOYEE = {
  nombre: '',
  puesto: '',
  rfc: '',
  curp: '',
  nss: '',
  correo: '',
  telefono: ''
};

export const FINIQUITOS_DEFAULT_SEPARATION = {
  motivo: 'renuncia_voluntaria',
  fechaCalculo: '',
  fechaIngreso: '',
  fechaAlta: '',
  fechaSeparacion: '',
  fechaFirma: '',
  ciudadFirma: '',
  estadoFirma: '',
  salarioDiario: '',
  aguinaldoDiasAnuales: '15',
  aguinaldoDiasLaborados: '',
  diasIndemnizacion: '',
  vacacionesNoPagadasDias: '',
  testigo1Nombre: '',
  testigo2Nombre: '',
  vacationTableMode: FINIQUITOS_VACATION_TABLE_MODES.legal,
  vacationTableOverrideReason: ''
};

export function isFormulaLikeValue(value) {
  return typeof value === 'string' && FORMULA_PREFIX.test(value);
}

export function normalizeDraftText(value, fieldLabel, { required = false } = {}) {
  const text = String(value ?? '').trim();

  if (!text) {
    if (required) {
      throw new Error(`${fieldLabel} es obligatorio.`);
    }
    return '';
  }

  if (isFormulaLikeValue(text)) {
    throw new Error(`${fieldLabel} no puede iniciar con "=".`);
  }

  return text;
}

export function normalizeDraftNumber(value, fieldLabel, { required = false, allowDecimals = true } = {}) {
  const text = String(value ?? '').trim();

  if (!text) {
    if (required) {
      throw new Error(`${fieldLabel} es obligatorio.`);
    }
    return null;
  }

  if (isFormulaLikeValue(text)) {
    throw new Error(`${fieldLabel} no puede iniciar con "=".`);
  }

  const sanitized = text.replace(/,/g, '');
  const parsed = Number(sanitized);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${fieldLabel} debe ser numérico.`);
  }
  if (parsed < 0) {
    throw new Error(`${fieldLabel} no puede ser negativo.`);
  }
  if (!allowDecimals && !Number.isInteger(parsed)) {
    throw new Error(`${fieldLabel} debe ser entero.`);
  }

  return parsed;
}

export function normalizeVacationTableMode(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'manual') {
    return FINIQUITOS_VACATION_TABLE_MODES.contractualManual;
  }
  if (normalized === 'override') {
    return FINIQUITOS_VACATION_TABLE_MODES.manualOverride;
  }
  if (
    normalized === FINIQUITOS_VACATION_TABLE_MODES.contractualManual ||
    normalized === FINIQUITOS_VACATION_TABLE_MODES.manualOverride
  ) {
    return normalized;
  }
  return FINIQUITOS_VACATION_TABLE_MODES.legal;
}

export function createEmptyConceptRow() {
  return {
    id: createStableId('concept'),
    label: '',
    amount: '',
    note: ''
  };
}

export function createConceptRowFromPreset(preset) {
  return {
    id: createStableId('concept'),
    label: preset?.label || '',
    amount: '',
    note: preset?.note || ''
  };
}

export function createEmptyBenefitRow() {
  return {
    id: createStableId('benefit'),
    type: '',
    label: '',
    amount: '',
    note: ''
  };
}

export function createBenefitRowFromPreset(preset) {
  return {
    id: createStableId('benefit'),
    type: preset?.type || '',
    label: preset?.label || '',
    amount: '',
    note: preset?.note || ''
  };
}

export function createVacationSpreadsheetData(rows = DEFAULT_VACATION_TABLE) {
  return rows.map(row => [
    { value: String(row.yearsWorked ?? '') },
    { value: String(row.vacationDays ?? '') }
  ]);
}

export function spreadsheetDataToVacationRows(data = []) {
  return data
    .map(row => {
      const yearsWorked = row?.[0]?.value ?? '';
      const vacationDays = row?.[1]?.value ?? '';
      return {
        yearsWorked,
        vacationDays
      };
    })
    .filter(row => String(row.yearsWorked ?? '').trim() || String(row.vacationDays ?? '').trim());
}

export function validateVacationRows(rows = []) {
  const errors = [];
  const normalizedRows = [];
  const seenYears = new Set();
  let previousYears = -Infinity;
  let previousDays = -Infinity;

  rows.forEach((row, index) => {
    const yearsText = String(row?.yearsWorked ?? '').trim();
    const daysText = String(row?.vacationDays ?? '').trim();
    const rowLabel = `vacaciones[${index + 1}]`;

    if (!yearsText && !daysText) {
      return;
    }

    if (isFormulaLikeValue(yearsText) || isFormulaLikeValue(daysText)) {
      errors.push(`${rowLabel} no puede contener fórmulas.`);
      return;
    }

    const yearsWorked = Number(yearsText.replace(/,/g, ''));
    const vacationDays = Number(daysText.replace(/,/g, ''));

    if (!Number.isFinite(yearsWorked) || !Number.isInteger(yearsWorked) || yearsWorked <= 0) {
      errors.push(`${rowLabel}.años debe ser un entero mayor que cero.`);
      return;
    }

    if (!Number.isFinite(vacationDays) || vacationDays < 0) {
      errors.push(`${rowLabel}.días debe ser un número no negativo.`);
      return;
    }

    if (seenYears.has(yearsWorked)) {
      errors.push(`vacaciones contiene años duplicados: ${yearsWorked}.`);
      return;
    }

    if (yearsWorked <= previousYears) {
      errors.push(`vacaciones debe ir en orden ascendente por año cumplido.`);
      return;
    }

    if (vacationDays < previousDays) {
      errors.push(`vacaciones no puede disminuir los días entre filas.`);
      return;
    }

    seenYears.add(yearsWorked);
    previousYears = yearsWorked;
    previousDays = vacationDays;
    normalizedRows.push({
      yearsWorked,
      vacationDays
    });
  });

  if (!normalizedRows.length) {
    errors.push('La tabla de vacaciones no puede estar vacía.');
  }

  if (!normalizedRows.length || normalizedRows[0].yearsWorked !== 1) {
    errors.push('La tabla de vacaciones debe comenzar en el año 1.');
  }

  return {
    errors,
    normalizedRows
  };
}

export function buildVacationConfigSnapshot({
  mode,
  overrideReason,
  allowManualOverride = false,
  rows = DEFAULT_VACATION_TABLE
} = {}) {
  const normalizedMode = normalizeVacationTableMode(mode);
  const normalizedRows = Array.isArray(rows)
    ? rows
        .map(row => ({
          anio: Number(row?.anio ?? row?.yearsWorked ?? row?.years ?? row?.year),
          dias: Number(row?.dias ?? row?.days ?? row?.vacationDays)
        }))
        .filter(row => Number.isFinite(row.anio) && Number.isFinite(row.dias))
    : [];

  return {
    mode: normalizedMode,
    tablaPorAnio: normalizedRows,
    allowManualOverride: normalizedMode === FINIQUITOS_VACATION_TABLE_MODES.manualOverride || Boolean(allowManualOverride),
    overrideReason: normalizeDraftText(overrideReason, 'Justificación del override manual') || ''
  };
}

export function normalizeConceptRows(rows = [], { sectionLabel, category, direction, requiresType = false }) {
  const errors = [];
  const normalizedRows = [];

  rows.forEach((row, index) => {
    const rowPrefix = `${sectionLabel || 'conceptos'}[${index + 1}]`;
    const rawLabel = String(row?.label ?? '').trim();
    const rawAmount = String(row?.amount ?? '').trim();
    const rawNote = String(row?.note ?? '').trim();
    const rawType = String(row?.type ?? '').trim();
    const hasAnyContent = Boolean(rawLabel || rawAmount || rawNote || rawType);

    if (!hasAnyContent) {
      return;
    }

    let hasRowError = false;
    let label = '';
    let amount = null;
    let note = '';
    let type = '';

    try {
      label = normalizeDraftText(row?.label, `${rowPrefix}.concepto`);
    } catch (error) {
      errors.push(error.message);
      hasRowError = true;
    }

    try {
      note = normalizeDraftText(row?.note, `${rowPrefix}.nota`);
    } catch (error) {
      errors.push(error.message);
      hasRowError = true;
    }

    try {
      type = normalizeDraftText(row?.type, `${rowPrefix}.tipo`);
    } catch (error) {
      errors.push(error.message);
      hasRowError = true;
    }

    if (requiresType && !rawType) {
      errors.push(`${rowPrefix}.tipo es obligatorio.`);
      hasRowError = true;
    }

    if (requiresType && type && !FINIQUITOS_BENEFIT_TYPE_VALUES.has(type)) {
      errors.push(
        `${rowPrefix}.tipo debe ser prestacion_superior o caso_excepcional.`
      );
      hasRowError = true;
    }

    try {
      amount = normalizeDraftNumber(row?.amount, `${rowPrefix}.monto`, {
        required: true
      });
    } catch (error) {
      errors.push(error.message);
      hasRowError = true;
    }

    if (!rawLabel) {
      errors.push(`${rowPrefix}.concepto es obligatorio.`);
      hasRowError = true;
    }

    if (hasRowError) {
      return;
    }

    normalizedRows.push({
      id: String(row?.id || createStableId(sectionLabel || 'concepto')).trim(),
      label,
      amount,
      note,
      category: requiresType ? type : category,
      direction
    });
  });

  return {
    errors,
    normalizedRows
  };
}

export function buildEmptyFiniquitoSnapshot() {
  return {
    employer: null,
    employee: { ...FINIQUITOS_DEFAULT_EMPLOYEE },
    separation: { ...FINIQUITOS_DEFAULT_SEPARATION },
    vacationTable: createVacationSpreadsheetData(DEFAULT_VACATION_TABLE),
    perceptions: [],
    deductions: [],
    benefits: []
  };
}
